<template>
    <!-- Prepaid series / cures the customer already owns, applied as payment on matching cart lines.
         Reuses getAllAvailableSeries / getAllAvailableTreatments + submitBasket's prepaid-serie[]/prepaid-treatment[]. -->
    <div v-if="applicable.length" class="cv3-special-block cv3-prepaid">
        <div class="cv3-special-head"><TicketIcon :size="16" /> {{ $t('checkout.v3.prepaid_title') }}</div>

        <div v-for="p in applicable" :key="p.kind + '-' + p.pivotId" class="cv3-prepaid-item">
            <div class="cv3-prepaid-item-head">
                <span class="cv3-prepaid-name">{{ p.name }}</span>
                <span class="cv3-prepaid-rem"
                    >{{ p.remaining }} {{ p.remaining > 1 ? $t('checkout.v3.sessions') : $t('checkout.v3.session') }}
                    {{ p.remaining > 1 ? $t('checkout.v3.remaining_f_plural') : $t('checkout.v3.remaining_f') }}</span
                >
            </div>
            <div v-for="row in p.rows" :key="row.key" class="cv3-prepaid-row">
                <BaseCheckbox
                    :value="!!sel[row.key]"
                    @input="toggle(row, $event)"
                    :label="row.lineName"
                    classAdd="cv3-prepaid-check"
                    has-border-checked
                />
                <span v-if="sel[row.key]" class="cv3-prepaid-qty">
                    <input type="number" min="1" :max="row.maxQty" :value="sel[row.key].qty" @input="setQty(row, $event)" />
                    <small>/ {{ row.lineQty }}</small>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { Ticket as TicketIcon } from 'lucide-vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'

const PRESTA_TYPE = 'App\\Models\\Prestation\\Prestation'

export default {
    name: 'PrepaidPanel',
    components: { TicketIcon, BaseCheckbox },
    props: {
        store: { type: Object, required: true },
        open: { type: Boolean, default: false },
    },
    data() {
        return {
            series: [],
            treatments: [],
            // keyed selection: `${kind}|${pivotId}|${rowId}` -> { qty }
            sel: {},
        }
    },
    computed: {
        // Each owned serie/treatment that has at least one matching cart line, with those lines.
        applicable() {
            const out = []
            const build = (items, kind) => {
                ;(items || []).forEach((p) => {
                    const rows = (this.store.lines || [])
                        .filter((l) => this.matchesLine(p, l))
                        .map((l) => ({
                            key: `${kind}|${p.pivot.id}|${l.rowId}`,
                            kind,
                            pivotId: p.pivot.id,
                            orderItem: l.rowId,
                            lineName: l.name,
                            lineQty: Number(l.qty) || 1,
                            linePrice: Number(l.price) || 0,
                            maxQty: Math.min(Number(l.qty) || 1, Number(p.pivot.quantity_left) || 1),
                        }))
                    if (rows.length) {
                        out.push({
                            kind,
                            pivotId: p.pivot.id,
                            name: p.name,
                            remaining: Number(p.pivot.quantity_left) || 0,
                            rows,
                        })
                    }
                })
            }
            build(this.series, 'serie')
            build(this.treatments, 'treatment')
            return out
        },
    },
    watch: {
        open(v) {
            if (v) this.load()
            else this.reset()
        },
        // Re-evaluate emitted payload whenever the selection changes.
        sel: {
            handler() {
                this.emitChange()
            },
            deep: true,
        },
    },
    methods: {
        async load() {
            this.reset()
            const c = this.store.currentCustomer
            // Walk-in / guest customers own no prepaid credit.
            if (!this.store.userId || !c || c === false || c.is_guest) return
            try {
                const { series, treatments } = await this.store.fetchPrepaid(this.store.userId)
                this.series = series || []
                this.treatments = treatments || []
            } catch (e) {
                this.series = []
                this.treatments = []
            }
        },
        reset() {
            this.series = []
            this.treatments = []
            this.sel = {}
            this.emitChange()
        },
        // Faithful port of the legacy Serie.vue canApplySerie / canApplyTreatment matching.
        matchesLine(p, l) {
            if (!l || l.item_type !== PRESTA_TYPE) return false
            const duration = l.duration
            const pTicket = !!p.is_ticket
            const lTicket = !!l.is_ticket
            const prestas = p.prestations || []
            if (duration) {
                return (
                    prestas.some((x) => x.pivot && x.pivot.prestation_id == l.item_id && x.pivot.duration == duration) &&
                    pTicket === lTicket
                )
            }
            // NB: mirrors the legacy precedence `(!serie.is_ticket) == elementCart.is_ticket`.
            return prestas.some((x) => x.id == l.item_id) && !pTicket === lTicket
        },
        toggle(row, isChecked) {
            if (isChecked) {
                this.$set(this.sel, row.key, { qty: row.lineQty })
            } else {
                this.$delete(this.sel, row.key)
            }
        },
        setQty(row, ev) {
            let q = parseInt(ev.target.value, 10)
            if (isNaN(q) || q < 1) q = 1
            if (q > row.maxQty) q = row.maxQty
            if (this.sel[row.key]) this.$set(this.sel[row.key], 'qty', q)
        },
        emitChange() {
            const prepaidSerie = []
            const prepaidTreatment = []
            let coverage = 0
            // Flatten all applicable rows so we can resolve a selection key -> its row (price/orderItem).
            const rowByKey = {}
            this.applicable.forEach((p) => p.rows.forEach((r) => (rowByKey[r.key] = r)))
            Object.keys(this.sel).forEach((key) => {
                const row = rowByKey[key]
                if (!row) return
                const qty = Number(this.sel[key].qty) || 0
                if (qty <= 0) return
                coverage += row.linePrice * qty
                if (row.kind === 'serie') prepaidSerie.push({ orderItem: row.orderItem, userSerie: row.pivotId, quantity: qty })
                else prepaidTreatment.push({ orderItem: row.orderItem, userTreatment: row.pivotId, quantity: qty })
            })
            this.$emit('change', { prepaidSerie, prepaidTreatment, coverage: Math.round(coverage * 100) / 100 })
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-prepaid {
    gap: 8px;
}
.cv3-prepaid-item {
    border-top: 1px solid var(--cv3-border);
    padding-top: 8px;
    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }
}
.cv3-prepaid-item-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}
.cv3-prepaid-name {
    font-size: 13px;
    font-weight: 700;
}
.cv3-prepaid-rem {
    font-size: 12px;
    color: var(--cv3-muted);
}
.cv3-prepaid-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 6px;
}
.cv3-prepaid-check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--cv3-text-2);
    cursor: pointer;
}
.cv3-prepaid-qty {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    small {
        color: var(--cv3-muted);
        font-size: 11px;
    }
    input {
        width: 56px;
        text-align: right;
        background: var(--cv3-input);
        border: 1px solid var(--cv3-border-input);
        border-radius: 8px;
        color: var(--cv3-text);
        padding: 5px 7px;
        font-size: 13px;
        font-weight: 700;
        -moz-appearance: textfield;
        appearance: none;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}
</style>
